/**
 * DoColumn — the "Do" surface of Two-Box Today (TASK-575): the RM's task list, split out of
 * the old ActionCenter. Shows the single "needs attention" view (overdue + due-today +
 * high-priority, deduped, most-urgent first) with the full TaskCard controls (Complete ·
 * Snooze · Edit), and docks any pinned canvas tiles. Signals live in the NOTICE column
 * (ChangeRadar + Alerts tab); only tasks get "done".
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pin, X, ShieldAlert, RotateCw, PanelRightClose } from 'lucide-react';
import TaskCard from '../tasks/TaskCard';
import { WidgetErrorState } from '../ui/WidgetErrorState';
import { useCanvasStore } from '../../stores/canvasStore';
import { LENS_LABEL, type DeskLens } from '../../hooks/useDeskLens';
import {
  useOverdueTasks,
  useTasksDueToday,
  useHighPriorityTasks,
  useCompleteTask,
  useSnoozeTask,
  useFailedLifeEventActions,
  useRetryFailedLifeEventAction,
} from '../../hooks/useTasksQuery';

// Short label for a dropped compliance action's task_type (TASK-576 F3).
const FAILED_ACTION_LABEL: Record<string, string> = {
  sow_review: 'Source-of-Wealth review',
  crs_recert: 'CRS/FATCA re-certification',
  suitability_reassessment: 'Suitability reassessment',
  task_compliance: 'Compliance review',
};

// onHide (TASK-600 review): the dashboard offers hiding the rail — it occupies
// canvas space; the preference is DashboardPage's to own/persist.
// deskLens (TASK-924): the lens the Signals rail is set to, owned by DashboardPage so both
// rails are always at the same desk.
export const DoColumn: React.FC<{ onHide?: () => void; deskLens?: DeskLens }> = ({ onHide, deskLens }) => {
  const navigate = useNavigate();

  // TASK-602 honest failure states: a 500 must never render as "No tasks need
  // attention" — surface isError and offer a retry.
  const overdueQ = useOverdueTasks(20);
  const dueTodayQ = useTasksDueToday();
  const highPriQ = useHighPriorityTasks(10);
  const { data: overdueData } = overdueQ;
  const { data: dueTodayData } = dueTodayQ;
  const { data: highPriData } = highPriQ;
  const anyError = overdueQ.isError || dueTodayQ.isError || highPriQ.isError;
  const anyLoading = overdueQ.isLoading || dueTodayQ.isLoading || highPriQ.isLoading;
  const retryTasks = () => {
    void overdueQ.refetch();
    void dueTodayQ.refetch();
    void highPriQ.refetch();
  };
  const completeTask = useCompleteTask();
  const snoozeTask = useSnoozeTask();

  // TASK-576 F3: dropped mandatory compliance actions awaiting recovery.
  const { data: failedActions } = useFailedLifeEventActions();
  const retryAction = useRetryFailedLifeEventAction();

  const tasks = useMemo(() => {
    const seen = new Set<string>();
    const merged: NonNullable<typeof overdueData>['tasks'] = [];
    for (const group of [overdueData?.tasks, dueTodayData?.tasks, highPriData?.tasks]) {
      for (const t of group ?? []) {
        if (!seen.has(t.id)) {
          seen.add(t.id);
          merged.push(t);
        }
      }
    }
    return merged;
  }, [overdueData, dueTodayData, highPriData]);

  /**
   * TASK-924: the same desk lens as the Signals rail, over the SIGNAL the work came from.
   * `subject_source_event` is the durable binding (TASK-724) and resolves through the server's
   * catalogue map, so a task and the row it was raised from can never land in different chips.
   *
   * Two kinds of task always show, and the distinction is the whole safety of this filter:
   * work that is bound to no signal at all (a meeting action, a manual task — it belongs to no
   * desk, so no desk's lens may hide it), and work whose signal this build cannot classify.
   * A lens that hides what it failed to identify hides work.
   */
  const lensMatches = deskLens?.matches;
  const lensedTasks = useMemo(
    () => (lensMatches ? tasks.filter((t) => !t.subject_source_event || lensMatches(t.subject_source_event)) : tasks),
    [tasks, lensMatches],
  );

  // Pinned canvas tiles dock here. Stable selector + filter in useMemo (a filter inside the
  // selector returns a new array every run and re-renders on every canvas change).
  const specs = useCanvasStore((s) => s.specs);
  const pinned = useMemo(() => specs.filter((tile) => tile.rail === 'left'), [specs]);
  const setRail = useCanvasStore((s) => s.setRail);

  return (
    // TASK-749: `xl:overflow-hidden` with no height bound never engages, so this rail grew to
    // fit its task list and took the whole PAGE with it. Capped and scrolling internally: the
    // page height stops being a function of how many tasks the account has, and nothing is
    // clipped — `overflow-y-auto` keeps every row reachable by wheel, scrollbar and keyboard.
    // The cap is xl-only; below that the layout is a stack where growing is correct.
    <aside
      aria-label="Actions"
      className="flex w-full xl:w-[280px] 2xl:w-[320px] shrink-0 flex-col gap-4 xl:max-h-[calc(100vh-14rem)] xl:overflow-y-auto"
    >
      {pinned.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Pin className="h-4 w-4 text-kielis-blue" />
            <h4 className="text-sm font-semibold text-gray-900">Pinned views</h4>
          </div>
          <div className="space-y-2">
            {pinned.map((tile) => (
              <div
                key={tile.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
              >
                <span className="truncate text-sm font-medium text-gray-800">{tile.component}</span>
                <button
                  type="button"
                  title="Unpin — return to canvas"
                  aria-label="Unpin view"
                  onClick={() => setRail(tile.id, null)}
                  className="text-gray-400 transition-colors hover:text-red-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {failedActions && failedActions.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-600" />
            <h4 className="text-sm font-semibold text-amber-900">
              Compliance actions to recover
            </h4>
            <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-200 px-1 text-[10px] font-semibold text-amber-800">
              {failedActions.length}
            </span>
          </div>
          <div className="space-y-2">
            {failedActions.map((f) => (
              <div
                key={`${f.event_id}:${f.task_type}`}
                className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-white px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-800">
                    {FAILED_ACTION_LABEL[f.task_type] ?? f.task_type}
                  </div>
                  <div className="truncate text-[11px] text-gray-500">
                    From a {f.event_type ?? 'life'} event · failed to create
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => retryAction.mutate(f.event_id)}
                  disabled={retryAction.isPending}
                  title="Retry — re-create the mandatory task"
                  className="flex shrink-0 items-center gap-1 rounded-md bg-amber-600 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
                >
                  <RotateCw className="h-3 w-3" />
                  Retry
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white shadow-sm max-h-[75vh] 2xl:max-h-none">
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Do</h3>
          {lensedTasks.length > 0 && (
            <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gray-200 px-1 text-[10px] font-semibold text-gray-700">
              {lensedTasks.length}
            </span>
          )}
          <button
            type="button"
            onClick={() => navigate('/tasks')}
            className="ml-auto text-[11px] text-gray-500 transition-colors hover:text-gray-800"
          >
            All tasks
          </button>
          {onHide && (
            <button
              type="button"
              onClick={onHide}
              title="Hide Do panel"
              aria-label="Hide Do panel"
              className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <PanelRightClose className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex-1 space-y-2 overflow-auto p-3">
          {anyError && (
            <WidgetErrorState
              title="Couldn't load tasks"
              detail="The task service didn't respond."
              onRetry={retryTasks}
            />
          )}
          {lensedTasks.length === 0 ? (
            anyLoading && !anyError ? (
              <div className="space-y-2 p-1" aria-hidden>
                <div className="h-14 animate-pulse rounded-lg bg-gray-100" />
                <div className="h-14 animate-pulse rounded-lg bg-gray-100" />
              </div>
            ) : (
              !anyError && (
                <div className="flex h-full items-center justify-center text-center">
                  <div className="text-xs text-gray-400">
                    <div className="mb-1 text-2xl">✓</div>
                    {/* TASK-924: a narrowed lens must never claim the book is clear. There IS
                        work; it is another desk's. Same rule as TASK-602's failure states —
                        "all caught up" is a strong claim and only the unlensed list may make it. */}
                    {tasks.length > 0
                      ? `No ${LENS_LABEL[deskLens?.lens ?? 'all'].toLowerCase()} work needs attention — ${tasks.length} on other desks.`
                      : 'No tasks need attention.'}
                  </div>
                </div>
              )
            )
          ) : (
            lensedTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                // TASK-922: a signal-bound task hands back the outcome the server
                // requires; a plain one still completes with nothing to say.
                // `mutateAsync`, not `mutate`: the card keeps the outcome form open on a
                // rejected completion, and it needs a promise to know that happened.
                onComplete={(id, submission) =>
                  completeTask.mutateAsync(submission ? { taskId: id, ...submission } : id)
                }
                onSnooze={(id, until) => snoozeTask.mutate({ taskId: id, snoozeUntil: until })}
                onEdit={() => navigate('/tasks')}
              />
            ))
          )}
        </div>
      </div>
    </aside>
  );
};

export default DoColumn;
